import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 左欄：Inbox 對話清單（未讀優先）。
 *
 * 每列＝一間房／一個身分的對話：房間名、未讀數、最後一則預覽、時間。
 * 未讀用色點＋數字標示；鍵盤 ↑/↓（Home/End）移動選取，不用滑鼠也能巡未讀。
 * 樣式沿用 styles.css 的 --bg/--fg/--muted/--border/--accent，深色介面。
 */
import { memo, useCallback, useEffect, useRef } from 'react';
import { findHighlights, splitByHighlights } from '../hooks/useHighlights';
import { normalizeMessageText } from '../message-text';
import { resolveDataState } from '../api/data-state';
import { healthStatusOf } from './HealthPanel';
import { DataStateNotice } from './DataStateNotice';
/**
 * 對話列的狀態點：在線綠、休眠黄、斷線紅（限流也是黄）。
 * 完全重用健康看板的判斷（`healthStatusOf`），差別只在後端有回報 `dormant` 時直接算休眠，
 * 不用等 10 分鐘的 idle 視窗——那個視窗是「後端沒說」時的猜法。
 */
export function conversationStatusOf(conversation, now = Date.now()) {
    if (conversation.dormant === true && !conversation.online) {
        return { tone: 'idle', label: '休眠', reason: '後端回報這個身分已休眠；送出前會自動重連' };
    }
    return healthStatusOf(conversation, now);
}
const timeFormat = new Intl.DateTimeFormat('zh-TW', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
const dateTimeFormat = new Intl.DateTimeFormat('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
/** 今天只顯示時分，其他日子顯示月/日 時分（避免清單每列都長一串）。 */
function formatWhen(ts) {
    if (!ts)
        return '';
    const date = new Date(ts);
    const now = new Date();
    const sameDay = date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();
    return (sameDay ? timeFormat : dateTimeFormat).format(date);
}
/**
 * 清單的「唯一 Tab 站點」：作用中的對話；沒有作用中的就用第一列。
 * 兩者都沒有（空清單）時回傳 undefined，呼叫端自然不會給 tabIndex=0。
 */
function rovingTabStopId(activeId, conversations) {
    if (activeId && conversations.some((item) => item.id === activeId))
        return activeId;
    return conversations[0]?.id;
}
/** 在清單裡找出某一列的 DOM（roving tabindex 搬焦點時要用）。 */
function findRow(list, id) {
    if (!list)
        return null;
    const rows = list.querySelectorAll('[data-conversation-id]');
    for (const row of Array.from(rows))
        if (row.dataset.conversationId === id)
            return row;
    return null;
}
/**
 * 單列對話（memo 化）。
 *
 * 為什麼要拆出來並包 memo：送出訊息時 `setConversations` 會換掉「那一列」的物件，
 * 但整份清單是新的陣列 → 父層每次都會 map 195 次。沒有 memo 時每一列都會重算
 * `findHighlights`／`splitByHighlights` 並產生新 JSX，實測一次 commit 300ms+。
 *
 * props 全部是「值」而不是每次新造的物件／函式，預設的 shallow compare 才真的命中：
 *   - `conversation`：useInbox 對無關的列回傳**同一個**物件（map 內 `return item`）→ 命中。
 *   - `keywords`：來自 useHighlights 的 useMemo，只有關鍵字真的變才換 identity。
 *   - `onSelect`：useConsoleActions 的 openConversation 是 useCallback（deps 穩定）。
 *   - `tone`／`statusTitle`：字串，狀態沒變就相等；**不要**傳 `status` 物件或 `now`，
 *     那兩個每次 render 都是新值，memo 會全面失效（等於沒加）。
 */
const ConversationRow = memo(function ConversationRow({ conversation, isActive, isTabStop, keywords, onSelect, tone, statusTitle, }) {
    const unread = conversation.unread > 0;
    // 預覽可能是站方的結構化系統訊息（`{"message":…}`）或帶 `img:<hash>`；先正規化再比對關鍵字，
    // 否則整列會露出原始 JSON、而且關鍵字永遠命中不到。
    const preview = normalizeMessageText(conversation.lastMessagePreview);
    // 命中關鍵字：只有「未讀」才標（已讀的對話你已經看過，不必再喊一次）。
    const hits = unread ? findHighlights(preview, keywords) : [];
    const highlighted = hits.length > 0;
    return (_jsx("li", { children: _jsxs("div", { role: "option", "aria-selected": isActive, tabIndex: isTabStop ? 0 : -1, className: `cui-conv${isActive ? ' is-active' : ''}${unread ? ' is-unread' : ''}${highlighted ? ' is-highlight' : ''}`, "data-testid": "conversation-row", "data-conversation-id": conversation.id, "data-unread": conversation.unread, "data-highlight": highlighted ? 'true' : undefined, onClick: () => onSelect(conversation.id), onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(conversation.id);
                }
            }, children: [_jsx("span", { className: `cui-unread-dot${unread ? ' is-on' : ''}`, "aria-hidden": "true", "data-testid": "unread-dot" }), _jsxs("span", { className: "cui-conv-main", children: [_jsxs("span", { className: "cui-conv-top", children: [_jsx("span", { className: `cui-health-dot cui-health-dot--${tone}`, "aria-hidden": "true", title: statusTitle, "data-testid": "health-dot", "data-tone": tone }), _jsx("span", { className: "cui-conv-room", "data-testid": "conversation-room", children: conversation.room ?? '（未進房）' }), conversation.roomOnline !== null ? (_jsxs("span", { className: "cui-conv-online cui-mono", children: [conversation.roomOnline, "\u4EBA\u5728\u7DDA"] })) : null, _jsx("span", { className: "cui-conv-time cui-mono", "data-testid": "conversation-time", children: formatWhen(conversation.lastActivityAt) })] }), _jsxs("span", { className: "cui-conv-bottom", children: [_jsx("span", { className: "cui-conv-name", children: conversation.nickname }), _jsx("span", { className: "cui-conv-preview", "data-testid": "conversation-preview", children: preview
                                        ? splitByHighlights(preview, keywords).map((segment, index) => segment.hit ? (_jsx("mark", { className: "cui-mark", children: segment.text }, index)) : (_jsx("span", { children: segment.text }, index)))
                                        : '—' }), highlighted ? (_jsx("span", { className: "cui-conv-highlight", title: `命中關鍵字：${hits.join('、')}`, "data-testid": "conversation-highlight", children: "\u95DC\u9375\u5B57" })) : null, unread ? (_jsx("span", { className: "cui-unread-count", "data-testid": "unread-count", children: conversation.unread })) : null] })] })] }) }));
});
/**
 * 清單本體也 memo：送出訊息的那個 commit 裡 `conversations` 還沒換（預覽更新是 transition），
 * props 全部沒變 → 整個左欄連 map 都不做，直接沿用上次的 subtree。
 */
export const ConversationList = memo(function ConversationList({ conversations, activeId = null, onSelect, title = '對話', emptyText = '還沒有對話；先建帳號並進房', loading = false, error = null, errorKind = null, loaded = true, onRetry = null, keywords = [], hiddenOnMobile = false, now = Date.now(), }) {
    const listRef = useRef(null);
    /**
     * 清單的唯一狀態判斷（#02）。順序在 `resolveDataState`：
     * 錯誤（連不上／401／5xx）先於「0 筆」，所以斷線時不會再顯示「還沒有對話」。
     */
    const state = resolveDataState({ loading, error, errorKind, count: conversations.length, loaded });
    /**
     * roving tabindex 的「唯一 Tab 站點」：作用中的那一列；沒有選取時退回第一列。
     * 選這個做法而不是 `aria-activedescendant`，是因為前者的焦點真的落在列元素上——
     * :focus-visible 由瀏覽器自己畫（不必手寫「假的焦點圈」），而且列上的 Enter／Space
     * handler 才會被真的呼叫到（走查缺陷 4 就是列 tabIndex=-1 導致那段是死碼）。
     * 容器本身 tabIndex=-1：Tab 進清單只停一次，不會先停容器再停列。
     */
    const tabStopId = rovingTabStopId(activeId, conversations);
    // 選取改變時把該列捲進視野；若焦點本來就在這個清單裡（方向鍵、Tab 跳未讀），
    // 連焦點一起搬到新的一列，否則「選取換了、焦點沒動」鍵盤使用者會卡在原地。
    useEffect(() => {
        if (!activeId)
            return;
        const row = findRow(listRef.current, activeId);
        if (!row)
            return;
        row.scrollIntoView({ block: 'nearest' });
        const focused = document.activeElement;
        if (!focused || focused === row)
            return;
        const list = listRef.current;
        if (list && (focused === list || list.contains(focused)))
            row.focus({ preventScroll: true });
    }, [activeId]);
    const move = useCallback((delta) => {
        if (conversations.length === 0)
            return;
        const current = conversations.findIndex((item) => item.id === activeId);
        const from = current === -1 ? 0 : current;
        const next = Math.min(conversations.length - 1, Math.max(0, from + delta));
        const target = conversations[next];
        if (target && target.id !== activeId)
            onSelect(target.id);
    }, [activeId, conversations, onSelect]);
    const jump = useCallback((edge) => {
        const target = edge === 'start' ? conversations[0] : conversations[conversations.length - 1];
        if (target)
            onSelect(target.id);
    }, [conversations, onSelect]);
    const unreadTotal = conversations.reduce((sum, item) => sum + item.unread, 0);
    return (_jsxs("section", { className: `cui-panel${hiddenOnMobile ? ' cui-pane--mobile-hidden' : ''}`, "data-testid": "conversation-list", "aria-label": title, children: [_jsxs("header", { className: "cui-panel-head", children: [_jsx("h2", { className: "cui-panel-title", children: title }), _jsx("span", { className: `cui-count${unreadTotal > 0 ? ' cui-count--unread' : ''}`, "data-testid": "unread-total", children: state === 'loading'
                            ? '載入中…'
                            : state === 'offline' || state === 'unauthorized' || state === 'error'
                                ? '—'
                                : unreadTotal > 0
                                    ? `${unreadTotal} 則未讀`
                                    : `${conversations.length} 個對話` })] }), state !== 'ready' ? (_jsx(DataStateNotice, { state: state, message: error, onRetry: onRetry, emptyText: emptyText, emptyHint: state === 'empty' ? '先確認右上角身分與站別；沒有對話不代表帳號沒建好。' : undefined, loadingText: "\u8B80\u53D6\u5C0D\u8A71\u6E05\u55AE\u2026", testId: "conversation-state" })) : null, conversations.length > 0 ? (_jsx("ul", { className: "cui-list cui-conv-list", role: "listbox", "aria-label": `${title}（↑↓ 切換、Enter 開啟）`, 
                /*
                 * 容器 tabIndex=-1：Tab 只停在「作用中那一列」（roving tabindex），
                 * 不讓 Tab 先停容器再停列（兩個站點會讓人多按一次才進得了清單）。
                 */
                tabIndex: -1, ref: listRef, "data-testid": "conversation-options", onKeyDown: (event) => {
                    switch (event.key) {
                        case 'ArrowDown':
                        case 'j':
                            event.preventDefault();
                            move(1);
                            break;
                        case 'ArrowUp':
                        case 'k':
                            event.preventDefault();
                            move(-1);
                            break;
                        case 'Home':
                            event.preventDefault();
                            jump('start');
                            break;
                        case 'End':
                            event.preventDefault();
                            jump('end');
                            break;
                        default:
                            break;
                    }
                }, children: conversations.map((conversation) => {
                    // 狀態點在父層算（`now` 只在這裡更新，不會傳進 memo 元件而破壞比對）。
                    const status = conversationStatusOf(conversation, now);
                    return (_jsx(ConversationRow, { conversation: conversation, isActive: conversation.id === activeId, isTabStop: conversation.id === tabStopId, keywords: keywords, onSelect: onSelect, tone: status.tone, statusTitle: `${status.label}：${status.reason}` }, conversation.id));
                }) })) : null] }));
});
//# sourceMappingURL=ConversationList.js.map